import type { NotificationsUserQuery } from '@api/dto/gql-helpers.generated';
import {
  useNotificationsUserClearAllMutation,
  useNotificationsUserLazyQuery,
} from '@api/dto/gql-helpers.generated';
import { useCallback, useEffect, useState } from 'react';

import { Button, Drawer, Stack, lighten } from '@mui/material';

import { MessageList } from '@components/notificationDrawer/messageList';
import { ResizeObservers } from '@components/resizeObservers';

import { useApp } from '@system/app';

const drawerWidth = 350;

export const NotificationDrawer = () => {
  const { notificationUI, confirmDialog } = useApp();
  const { drawer: open } = notificationUI.use();

  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [items, setItems] = useState<
    NotificationsUserQuery['notification']['messages_user']['items']
  >([]);

  const [getNotificationUser, { loading }] = useNotificationsUserLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const clearState = () => {
    setItems([]);
    setTotalCount(0);
    setPage(0);
  };
  const [clearAll, { loading: loadingClearAll }] =
    useNotificationsUserClearAllMutation({
      onCompleted: () => {
        void getNotificationUser();
      },
    });

  useEffect(() => {
    if (open) {
      getNotificationUser({
        variables: {
          paginate: {
            per_page: 50,
            page,
          },
        },
      }).then(response => {
        if (response.data) {
          const { items, totalCount } =
            response.data.notification.messages_user;
          setTotalCount(totalCount);
          setItems(prevState => [...prevState, ...items]);
        }
      });
    }
    if (!open) {
      clearState();
    }
  }, [getNotificationUser, open, page]);

  useEffect(() => {
    return () => {
      clearState();
    };
  }, []);

  const handleLoadMore = useCallback((isScreen: boolean) => {
    if (isScreen) {
      setPage(prevPage => prevPage + 1);
    }
  }, []);

  const handleClearAll = () => {
    confirmDialog.set({
      title: 'Очистить все уведомления',
      message: 'Вы уверены, что хотите удалить все уведомления?',
      onConfirm: () => {
        void clearAll();
        notificationUI.toggle();
        clearState();
        return true;
      },
    });
  };

  return (
    <Drawer
      open={open}
      variant="persistent"
      anchor="right"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          boxShadow: '0px 0px 8px 0px #384b5821',
          width: drawerWidth,
          borderLeft: 'none',
          boxSizing: 'border-box',
          top: '30px',
          pb: 13,
          pt: 1,
          pl: 1,
          '& .v-list::-webkit-scrollbar': {
            background: lighten('#394A58', 0.8),
            width: '10px',
          },
          '& .v-list::-webkit-scrollbar-track': {
            background: lighten('#394A58', 0.8),
          },
          '& .v-list::-webkit-scrollbar-thumb': {
            background: '#394A58',
            borderRadius: 2,
          },
        },
      }}
    >
      <ResizeObservers>
        {rect => (
          <MessageList
            rect={rect}
            items={items}
            loading={loading}
            onLoadMore={handleLoadMore}
            hasNextPage={(page + 1) * 20 < totalCount}
          />
        )}
      </ResizeObservers>

      <Stack
        sx={{
          position: 'fixed',
          bottom: 0,
          backgroundColor: 'white',
          width: '350px',
          justifyContent: 'center',
          borderTop: p => `1px solid ${p.palette.divider}`,
        }}
      >
        <Button
          onClick={handleClearAll}
          size="small"
          disabled={loadingClearAll || items.length === 0}
          variant="text"
        >
          Очистить
        </Button>
      </Stack>
    </Drawer>
  );
};
